import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { ActivityIndicator, Text, TextInput } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AuthStackParamList, RootStackParamList } from '../../navigation/types';
import { useAppColors } from '../../hooks/useAppColors';
import {
  useSendVerificationEmail,
  useVerifyEmail,
  useCompleteRegistration,
  syncWithBackend,
} from '../../hooks/useAuth';
import { authClient } from '../../lib/auth-client';
import { useAuthStore } from '../../stores/authStore';
import { useRoleStore } from '../../stores/roleStore';
import { getApiError } from '../../utils/formatters';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AuthLayout from './AuthLayout';

type Props = NativeStackScreenProps<AuthStackParamList & RootStackParamList, 'EmailVerification'>;

export default function EmailVerificationScreen({ navigation, route }: Props) {
  const { t } = useTranslation();
  const colors = useAppColors();
  const sendVerification = useSendVerificationEmail();
  const verifyEmail = useVerifyEmail();
  const completeReg = useCompleteRegistration();
  const setAuth = useAuthStore((s) => s.setAuth);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);

  const { email, role, first_name, last_name, birthday, terms_accepted, weight_kg, height_cm, measurement_unit } = route.params;
  const isRegistration = !!(role && first_name && last_name);

  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const sentRef = useRef(false);
  const finishingRef = useRef(false);

  // Complete the post-verification steps (backend sync or registration).
  // Called after a successful OTP, and also when the account turns out to be
  // already verified server-side — e.g. verified in a session that was killed
  // before the local flag was saved, which otherwise strands the user here.
  // finishingRef guards against the mount check and a concurrent manual code
  // submission both completing (double sync / double registration).
  const finishVerified = async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    const storageKey = `email_verification_last_sent_${email.toLowerCase()}`;
    await AsyncStorage.removeItem(storageKey);

    if (isRegistration) {
      completeReg.mutate(
        { role, first_name, last_name, dob: birthday, terms_accepted, weight_kg, height_cm, measurement_unit },
        {
          onSuccess: () => {
            // useCompleteRegistration's onSuccess already calls setAuth with emailVerified=true
            // RootNavigator will auto-switch based on needsOnboarding + emailVerified
          },
          onError: (err: any) => {
            finishingRef.current = false;
            setVerifying(false);
            setError(getApiError(err, t('auth.registrationFailed'), t));
          },
        },
      );
    } else {
      try {
        const sync = await syncWithBackend();
        const needsCompletion =
          !sync.user.dob ||
          !sync.user.terms_accepted_at;
        setAuth(sync.access_token, sync.user, needsCompletion, true);
        setActiveRole(sync.user.role);
        // RootNavigator will auto-switch
      } catch (err: any) {
        finishingRef.current = false;
        setVerifying(false);
        setError(err.message || t('auth.syncFailed'));
      }
    }
  };

  // Returns true when the auth provider says this account is already verified
  // (requires an existing session — absent on the plain login flow, where this
  // safely reports false).
  const isAlreadyVerified = async (): Promise<boolean> => {
    try {
      const session = await (authClient as any).getSession?.();
      return session?.data?.user?.emailVerified === true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    // Escape hatch: if the server already considers this account verified,
    // don't make the user hunt for a code that will never arrive.
    const checkAlreadyVerified = async () => {
      if (await isAlreadyVerified()) {
        setVerifying(true);
        await finishVerified();
      }
    };
    checkAlreadyVerified();
  }, []);

  useEffect(() => {
    const checkCooldownAndSend = async () => {
      const storageKey = `email_verification_last_sent_${email.toLowerCase()}`;
      const lastSent = await AsyncStorage.getItem(storageKey);
      
      if (lastSent) {
        const elapsed = Date.now() - parseInt(lastSent, 10);
        const remaining = 60 - Math.floor(elapsed / 1000);
        
        if (remaining > 0) {
          setSent(true);
          setCountdown(remaining);
          return;
        }
      }
      
      // Registration flow: signUp.email already sent the verification email,
      // so just mark as sent and start the resend cooldown without sending again.
      if (isRegistration) {
        if (!sentRef.current) {
          sentRef.current = true;
          setSent(true);
          await AsyncStorage.setItem(storageKey, Date.now().toString());
          setCountdown(60);
        }
        return;
      }
      
      // Login flow (unverified user): no email was sent yet, so send one now.
      if (!sentRef.current) {
        sentRef.current = true;
        handleResend();
      }
    };
    
    checkCooldownAndSend();
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResend = async () => {
    if (countdown > 0) return;
    setError('');
    sendVerification.mutate(email, {
      onSuccess: async () => {
        // Save timestamp for cooldown
        const storageKey = `email_verification_last_sent_${email.toLowerCase()}`;
        await AsyncStorage.setItem(storageKey, Date.now().toString());
        
        setSent(true);
        setCountdown(60);
      },
      onError: (err: any) => setError(err.message || t('auth.verificationSendFailed')),
    });
  };

  const handleVerify = () => {
    if (!code.trim()) {
      setError(t('auth.verificationCodeRequired'));
      return;
    }
    setError('');
    setVerifying(true);
    verifyEmail.mutate(
      { email, otp: code.trim() },
      {
        onSuccess: () => finishVerified(),
        onError: async (err: any) => {
          // A failing code on an already-verified account (the code was consumed
          // in an earlier session, or verification happened via the email link)
          // should proceed, not dead-end on "invalid code".
          if (await isAlreadyVerified()) {
            await finishVerified();
            return;
          }
          setVerifying(false);
          const msg = err.message || '';
          if (msg.toLowerCase().includes('too many') || msg.toLowerCase().includes('rate limit')) {
            setError(t('auth.tooManyRequests'));
          } else {
            setError(msg || t('auth.invalidVerificationCode'));
          }
        },
      },
    );
  };

  const inputTheme = {
    colors: {
      onSurfaceVariant: colors.textSecondary,
      surface: colors.card,
    },
  };

  return (
    <AuthLayout
      title={t('auth.verifyEmail')}
      subtitle={t('auth.verifyEmailSubtitle', { email })}
    >
      {sent && (
        <Text style={[styles.sentText, { color: colors.success }]}>
          {t('auth.verificationEmailSent')}
        </Text>
      )}

      <TextInput
        label={t('auth.verificationCode')}
        value={code}
        onChangeText={(v) => {
          setCode(v.replace(/\D/g, '').slice(0, 6));
          if (error) setError('');
        }}
        mode="outlined"
        keyboardType="number-pad"
        maxLength={6}
        autoComplete="one-time-code"
        style={styles.codeInput}
        outlineColor={colors.border}
        activeOutlineColor={colors.accent}
        textColor={colors.text}
        theme={inputTheme}
        autoFocus
      />

      {!!error && (
        <Text style={[styles.error, { color: colors.error }]}>{error}</Text>
      )}

      <TouchableOpacity
        style={[styles.button, { backgroundColor: colors.accent }]}
        onPress={handleVerify}
        disabled={verifying || verifyEmail.isPending || completeReg.isPending}
        activeOpacity={0.8}
      >
        {(verifying || verifyEmail.isPending || completeReg.isPending) ? (
          <ActivityIndicator size="small" color={colors.accentText} />
        ) : (
          <Text style={[styles.buttonText, { color: colors.accentText }]}>
            {t('auth.verifyCode')}
          </Text>
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={styles.linkButton}
        onPress={handleResend}
        disabled={countdown > 0 || sendVerification.isPending}
      >
        <Text style={[styles.linkText, { color: countdown > 0 ? colors.textTertiary : colors.accent }]}>
          {countdown > 0
            ? t('auth.resendIn', { seconds: countdown })
            : sendVerification.isPending
              ? t('common.loading')
              : t('auth.resendVerification')}
        </Text>
      </TouchableOpacity>

      <TouchableOpacity style={styles.linkButton} onPress={() => {
        console.log('[EmailVerification] Back to Sign In pressed');
        clearAuth();
        // Reset navigation to Login - works from both AuthNavigator and RootNavigator
        navigation.reset({
          index: 0,
          routes: [{ name: 'Login' }],
        });
      }}>
        <Text style={[styles.linkText, { color: colors.accent }]}>{t('auth.backToSignIn')}</Text>
      </TouchableOpacity>
    </AuthLayout>
  );
}

const styles = StyleSheet.create({
  sentText: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
  },
  codeInput: {
    marginBottom: 16,
    backgroundColor: 'transparent',
    fontSize: 24,
    textAlign: 'center',
    letterSpacing: 8,
  },
  error: {
    textAlign: 'center',
    marginBottom: 12,
    fontSize: 13,
  },
  button: {
    marginTop: 4,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  linkButton: {
    marginTop: 20,
    alignItems: 'center',
  },
  linkText: {
    fontSize: 14,
  },
});
